import { DEFAULT_ACCEPTABLE_PRICE_IMPACT_BUFFER } from "configs/factors";
import { bigMath } from "utils/bigmath";
import { getPositionFee } from "utils/fees";
import { MarketInfo, MarketsInfoData } from "utils/markets/types";
import {
  applyFactor,
  BASIS_POINTS_DIVISOR_BIGINT,
  expandDecimals,
  getBasisPoints,
  roundUpDivision,
  USD_DECIMALS,
  MaxUint256,
} from "utils/numbers";
import { DecreasePositionSwapType, OrderType, SwapPricingType } from "utils/orders/types";
import {
  getLeverage,
  getLiquidationPrice,
  getMinCollateralFactorForPosition,
  getMinCollateralUsdForLiquidationPrice,
  getNetPriceImpactDeltaUsdForDecrease,
  getPositionPnlUsd,
} from "utils/positions";
import { PositionInfo, PositionInfoLoaded } from "utils/positions/types";
import {
  getAcceptablePriceInfo,
  getDefaultAcceptablePriceImpactBps,
  getMarkPrice,
  getOrderThresholdType,
} from "utils/prices";
import { UserReferralInfo } from "utils/referrals/types";
import { getSwapAmountsByFromValue, getSwapStats } from "utils/swap";
import { convertToTokenAmount, convertToUsd, getIsEquivalentTokens } from "utils/tokens";
import { TokenData } from "utils/tokens/types";

import { DecreasePositionAmounts, FindSwapPath, NextPositionValues, SwapAmounts } from "./types";

export function getDecreasePositionAmounts(p: {
  marketInfo: MarketInfo;
  collateralToken: TokenData;
  isLong: boolean;
  position: PositionInfoLoaded | undefined;
  closeSizeUsd: bigint;
  keepLeverage: boolean;
  triggerPrice?: bigint;
  fixedAcceptablePriceImpactBps?: bigint;
  acceptablePriceImpactBuffer?: number;
  userReferralInfo: UserReferralInfo | undefined;
  minCollateralUsd: bigint;
  minPositionSizeUsd: bigint;
  uiFeeFactor: bigint;
  isLimit?: boolean;
  limitPrice?: bigint;
  triggerOrderType?: DecreasePositionAmounts["triggerOrderType"];
  isSetAcceptablePriceImpactEnabled: boolean;

  receiveToken?: TokenData;
  forceDecreaseSwapType?: DecreasePositionSwapType;
}) {
  const {
    marketInfo,
    collateralToken,
    isLong,
    position,
    closeSizeUsd,
    keepLeverage,
    triggerPrice,
    fixedAcceptablePriceImpactBps,
    acceptablePriceImpactBuffer,
    userReferralInfo,
    minCollateralUsd,
    minPositionSizeUsd,
    uiFeeFactor,
    triggerOrderType: orderType,
    receiveToken: receiveTokenArg,
    isSetAcceptablePriceImpactEnabled,
    forceDecreaseSwapType,
  } = p;

  const { indexToken } = marketInfo;
  const receiveToken = receiveTokenArg ?? collateralToken;

  const values: DecreasePositionAmounts = {
    isFullClose: false,
    sizeDeltaUsd: 0n,
    sizeDeltaInTokens: 0n,
    collateralDeltaUsd: 0n,
    collateralDeltaAmount: 0n,

    indexPrice: 0n,
    collateralPrice: 0n,
    triggerPrice: 0n,
    acceptablePrice: 0n,

    proportionalPendingImpactDeltaUsd: 0n,
    closePriceImpactDeltaUsd: 0n,
    totalPendingImpactDeltaUsd: 0n,
    priceImpactDiffUsd: 0n,
    balanceWasImproved: false,
    acceptablePriceDeltaBps: 0n,
    recommendedAcceptablePriceDeltaBps: 0n,

    estimatedPnl: 0n,
    estimatedPnlPercentage: 0n,
    realizedPnl: 0n,
    realizedPnlPercentage: 0n,

    positionFeeUsd: 0n,
    uiFeeUsd: 0n,
    swapUiFeeUsd: 0n,
    borrowingFeeUsd: 0n,
    fundingFeeUsd: 0n,
    feeDiscountUsd: 0n,
    swapProfitFeeUsd: 0n,
    swapProfitUsdIn: 0n,
    payedOutputUsd: 0n,
    payedRemainingCollateralUsd: 0n,
    payedRemainingCollateralAmount: 0n,

    receiveTokenAmount: 0n,
    receiveUsd: 0n,

    primaryOutput: { tokenAddress: "", amount: 0n, usd: 0n },
    secondaryOutput: { tokenAddress: "", amount: 0n, usd: 0n },

    triggerOrderType: orderType,
    triggerThresholdType: undefined,
    decreaseSwapType: DecreasePositionSwapType.NoSwap,
  };

  const pnlToken = isLong ? marketInfo.longToken : marketInfo.shortToken;

  values.decreaseSwapType = forceDecreaseSwapType ?? getDecreaseSwapType(pnlToken, collateralToken, receiveToken);

  const markPrice = getMarkPrice({ prices: indexToken.prices, isIncrease: false, isLong });
  const isTrigger = orderType !== undefined;

  if (orderType) {
    values.triggerPrice = triggerPrice;
    values.indexPrice = triggerPrice ?? markPrice;

    values.collateralPrice = getIsEquivalentTokens(indexToken, collateralToken)
      ? triggerPrice ?? markPrice
      : collateralToken.prices.minPrice;

    values.triggerThresholdType = getOrderThresholdType(orderType, isLong);
  } else {
    values.indexPrice = markPrice;
    values.collateralPrice = collateralToken.prices.minPrice;
  }

  if (closeSizeUsd <= 0) {
    return values;
  }

  values.sizeDeltaUsd = closeSizeUsd;

  if (!position || position.sizeInUsd <= 0 || position.sizeInTokens <= 0) {
    applyAcceptablePrice({
      position,
      marketInfo,
      isLong,
      isTrigger,
      fixedAcceptablePriceImpactBps,
      acceptablePriceImpactBuffer,
      values,
      isSetAcceptablePriceImpactEnabled,
    });

    const positionFeeInfo = getPositionFee(
      marketInfo,
      values.sizeDeltaUsd,
      values.balanceWasImproved,
      userReferralInfo
    );

    values.positionFeeUsd = positionFeeInfo.positionFeeUsd;
    values.feeDiscountUsd = positionFeeInfo.discountUsd;
    values.uiFeeUsd = applyFactor(values.sizeDeltaUsd, uiFeeFactor);

    const totalFeesUsd = getTotalFeesUsdForDecrease({
      positionFeeUsd: values.positionFeeUsd,
      borrowingFeeUsd: 0n,
      fundingFeeUsd: 0n,
      swapProfitFeeUsd: 0n,
      swapUiFeeUsd: 0n,
      uiFeeUsd: values.uiFeeUsd,
      pnlUsd: 0n,
      totalPendingImpactDeltaUsd: values.totalPendingImpactDeltaUsd,
    });

    values.payedOutputUsd = totalFeesUsd;

    return values;
  }

  const estimatedCollateralUsd = convertToUsd(
    position.collateralAmount,
    collateralToken.decimals,
    values.collateralPrice
  )!;

  let estimatedCollateralDeltaUsd = 0n;

  if (keepLeverage) {
    estimatedCollateralDeltaUsd = bigMath.mulDiv(values.sizeDeltaUsd, estimatedCollateralUsd, position.sizeInUsd);
  }

  values.isFullClose = getIsFullClose({
    position,
    sizeDeltaUsd: values.sizeDeltaUsd,
    indexPrice: values.indexPrice,
    remainingCollateralUsd: estimatedCollateralUsd - estimatedCollateralDeltaUsd,
    minCollateralUsd,
    minPositionSizeUsd,
  });

  if (values.isFullClose) {
    values.sizeDeltaUsd = position.sizeInUsd;
    values.sizeDeltaInTokens = position.sizeInTokens;
  } else {
    if (position.isLong) {
      values.sizeDeltaInTokens = roundUpDivision(position.sizeInTokens * values.sizeDeltaUsd, position.sizeInUsd);
    } else {
      values.sizeDeltaInTokens = bigMath.mulDiv(position.sizeInTokens, values.sizeDeltaUsd, position.sizeInUsd);
    }
  }

  // PNL
  values.estimatedPnl = getPositionPnlUsd({
    marketInfo,
    sizeInUsd: position.sizeInUsd,
    sizeInTokens: position.sizeInTokens,
    markPrice: values.indexPrice,
    isLong,
  });

  values.realizedPnl = bigMath.mulDiv(values.estimatedPnl, values.sizeDeltaInTokens, position.sizeInTokens);
  values.realizedPnlPercentage =
    estimatedCollateralUsd !== 0n ? getBasisPoints(values.realizedPnl, estimatedCollateralUsd) : 0n;
  values.estimatedPnlPercentage =
    estimatedCollateralUsd !== 0n ? getBasisPoints(values.estimatedPnl, estimatedCollateralUsd) : 0n;

  applyAcceptablePrice({
    position,
    marketInfo,
    isLong,
    isTrigger,
    fixedAcceptablePriceImpactBps,
    acceptablePriceImpactBuffer,
    values,
    isSetAcceptablePriceImpactEnabled,
  });

  // Profit
  let profitUsd = 0n;
  if (values.realizedPnl > 0) {
    profitUsd = profitUsd + values.realizedPnl;
  }
  if (values.totalPendingImpactDeltaUsd > 0) {
    profitUsd = profitUsd + values.totalPendingImpactDeltaUsd;
  }
  const profitAmount = convertToTokenAmount(profitUsd, collateralToken.decimals, values.collateralPrice)!;

  // Fees
  const positionFeeInfo = getPositionFee(marketInfo, values.sizeDeltaUsd, values.balanceWasImproved, userReferralInfo);
  const estimatedPositionFeeCost = estimateCollateralCost(
    positionFeeInfo.positionFeeUsd,
    collateralToken,
    values.collateralPrice
  );
  const estimatedDiscountCost = estimateCollateralCost(
    positionFeeInfo.discountUsd,
    collateralToken,
    values.collateralPrice
  );

  values.positionFeeUsd = estimatedPositionFeeCost.usd;
  values.feeDiscountUsd = estimatedDiscountCost.usd;
  values.uiFeeUsd = applyFactor(values.sizeDeltaUsd, uiFeeFactor);

  const borrowFeeCost = estimateCollateralCost(
    position.pendingBorrowingFeesUsd,
    collateralToken,
    values.collateralPrice
  );

  values.borrowingFeeUsd = borrowFeeCost.usd;

  const fundingFeeCost = estimateCollateralCost(
    position.pendingFundingFeesUsd,
    collateralToken,
    values.collateralPrice
  );

  values.fundingFeeUsd = fundingFeeCost.usd;

  if (
    profitUsd > 0 &&
    (values.decreaseSwapType === DecreasePositionSwapType.SwapPnlTokenToCollateralToken ||
      values.decreaseSwapType === DecreasePositionSwapType.SwapCollateralTokenToPnlToken)
  ) {
    const isSwapToCollateral = values.decreaseSwapType === DecreasePositionSwapType.SwapPnlTokenToCollateralToken;

    const swapProfitStats = getSwapStats({
      marketInfo,
      tokenInAddress: isSwapToCollateral ? pnlToken.address : collateralToken.address,
      tokenOutAddress: isSwapToCollateral ? collateralToken.address : pnlToken.address,
      usdIn: profitUsd,
      shouldApplyPriceImpact: true,
      swapPricingType: SwapPricingType.Swap,
    });

    values.swapProfitFeeUsd = swapProfitStats.swapFeeUsd - swapProfitStats.priceImpactDeltaUsd;
    values.swapProfitUsdIn = swapProfitStats.usdIn;
    values.swapUiFeeUsd = applyFactor(swapProfitStats.usdIn, uiFeeFactor);
  } else {
    values.swapProfitFeeUsd = 0n;
    values.swapProfitUsdIn = 0n;
  }

  const totalFeesUsd = getTotalFeesUsdForDecrease({
    positionFeeUsd: values.positionFeeUsd,
    borrowingFeeUsd: values.borrowingFeeUsd,
    fundingFeeUsd: values.fundingFeeUsd,
    swapProfitFeeUsd: values.swapProfitFeeUsd,
    swapUiFeeUsd: values.swapUiFeeUsd,
    uiFeeUsd: values.uiFeeUsd,
    pnlUsd: values.realizedPnl,
    totalPendingImpactDeltaUsd: values.totalPendingImpactDeltaUsd,
  });

  const payedInfo = payForCollateralCost({
    initialCostUsd: totalFeesUsd,
    collateralToken,
    collateralPrice: values.collateralPrice,
    outputAmount: profitAmount,
    remainingCollateralAmount: position.collateralAmount,
  });

  values.payedOutputUsd = convertToUsd(payedInfo.paidOutputAmount, collateralToken.decimals, values.collateralPrice)!;
  values.payedRemainingCollateralAmount = payedInfo.paidRemainingCollateralAmount;
  values.payedRemainingCollateralUsd = convertToUsd(
    payedInfo.paidRemainingCollateralAmount,
    collateralToken.decimals,
    values.collateralPrice
  )!;

  let netCollateralOutputAmount = 0n;
  let netCollateralOutputUsd = 0n;

  // Collateral delta
  if (values.isFullClose) {
    values.collateralDeltaUsd = estimatedCollateralUsd;
    values.collateralDeltaAmount = position.collateralAmount;
    netCollateralOutputAmount = payedInfo.remainingCollateralAmount;
    netCollateralOutputUsd = convertToUsd(netCollateralOutputAmount, collateralToken.decimals, values.collateralPrice)!;
    values.receiveTokenAmount = payedInfo.outputAmount + payedInfo.remainingCollateralAmount;
  } else if (
    keepLeverage &&
    position.sizeInUsd > 0 &&
    estimatedCollateralUsd > 0 &&
    payedInfo.remainingCollateralAmount > 0
  ) {
    const remainingCollateralUsd = convertToUsd(
      payedInfo.remainingCollateralAmount,
      collateralToken.decimals,
      values.collateralPrice
    )!;
    const nextSizeInUsd = position.sizeInUsd - values.sizeDeltaUsd;
    const leverageWithoutPnl = getLeverage({
      sizeInUsd: position.sizeInUsd,
      collateralUsd: estimatedCollateralUsd,
      pendingBorrowingFeesUsd: position.pendingBorrowingFeesUsd,
      pendingFundingFeesUsd: position.pendingFundingFeesUsd,
      pnl: undefined,
    });

    values.collateralDeltaUsd =
      /**
       * 1. @see https://app.asana.com/0/1204313444805313/1207549197964321/f
       * 2. leverageWithoutPnl may be zero if sizeInUsd is defaulted to 0n when position not ready yet
       */
      leverageWithoutPnl !== undefined && leverageWithoutPnl !== 0n
        ? remainingCollateralUsd - bigMath.mulDiv(nextSizeInUsd, BASIS_POINTS_DIVISOR_BIGINT, leverageWithoutPnl)
        : 0n;
    values.collateralDeltaAmount = convertToTokenAmount(
      values.collateralDeltaUsd,
      collateralToken.decimals,
      values.collateralPrice
    )!;
    netCollateralOutputAmount = values.collateralDeltaAmount;
    netCollateralOutputUsd = values.collateralDeltaUsd;
    values.receiveTokenAmount = payedInfo.outputAmount + values.collateralDeltaAmount;
  } else {
    values.collateralDeltaUsd = 0n;
    values.collateralDeltaAmount = 0n;
    values.receiveTokenAmount = payedInfo.outputAmount;
  }

  values.receiveUsd = convertToUsd(values.receiveTokenAmount, collateralToken.decimals, values.collateralPrice)!;

  // SwapCollateralTokenToPnlToken → primaryOutput (pnl token), otherwise → secondaryOutput (collateral token)
  const pnlTokenPrice = pnlToken.prices.minPrice;
  const remainingPnlOutputUsd =
    convertToUsd(payedInfo.outputAmount, collateralToken.decimals, values.collateralPrice) ?? 0n;

  if (
    values.decreaseSwapType === DecreasePositionSwapType.NoSwap &&
    !getIsEquivalentTokens(pnlToken, collateralToken)
  ) {
    values.primaryOutput = {
      tokenAddress: pnlToken.address,
      amount: convertToTokenAmount(remainingPnlOutputUsd, pnlToken.decimals, pnlTokenPrice) ?? 0n,
      usd: remainingPnlOutputUsd,
    };
    values.secondaryOutput = {
      tokenAddress: collateralToken.address,
      amount: netCollateralOutputAmount,
      usd: netCollateralOutputUsd,
    };
  } else if (values.decreaseSwapType === DecreasePositionSwapType.SwapCollateralTokenToPnlToken) {
    values.primaryOutput = {
      tokenAddress: pnlToken.address,
      amount: convertToTokenAmount(values.receiveUsd, pnlToken.decimals, pnlTokenPrice) ?? 0n,
      usd: values.receiveUsd,
    };
    values.secondaryOutput = { tokenAddress: collateralToken.address, amount: 0n, usd: 0n };
  } else {
    values.primaryOutput = { tokenAddress: pnlToken.address, amount: 0n, usd: 0n };
    values.secondaryOutput = {
      tokenAddress: collateralToken.address,
      amount: values.receiveTokenAmount,
      usd: values.receiveUsd,
    };
  }

  return values;
}

export function getIsFullClose(p: {
  position: PositionInfoLoaded;
  sizeDeltaUsd: bigint;
  indexPrice: bigint;
  remainingCollateralUsd: bigint;
  minCollateralUsd: bigint;
  minPositionSizeUsd: bigint;
}) {
  const { position, sizeDeltaUsd, indexPrice, remainingCollateralUsd, minCollateralUsd, minPositionSizeUsd } = p;
  const { marketInfo, isLong } = position;

  if (position.sizeInUsd - sizeDeltaUsd < expandDecimals(1, USD_DECIMALS)) {
    return true;
  }

  const estimatedPnl = getPositionPnlUsd({
    marketInfo,
    sizeInUsd: position.sizeInUsd,
    sizeInTokens: position.sizeInTokens,
    markPrice: indexPrice,
    isLong,
  });

  const realizedPnl = bigMath.mulDiv(estimatedPnl, sizeDeltaUsd, position.sizeInUsd);

  const estimatedRemainingPnl = estimatedPnl - realizedPnl;

  if (realizedPnl < 0) {
    const estimatedRemainingCollateralUsd = remainingCollateralUsd - realizedPnl;

    let minCollateralFactor = isLong
      ? marketInfo.minCollateralFactorForOpenInterestLong
      : marketInfo.minCollateralFactorForOpenInterestShort;

    const minCollateralFactorForMarket = marketInfo.minCollateralFactor;

    if (minCollateralFactorForMarket > minCollateralFactor) {
      minCollateralFactor = minCollateralFactorForMarket;
    }

    const minCollateralUsdForLeverage = applyFactor(position.sizeInUsd, minCollateralFactor);
    const willCollateralBeSufficient = estimatedRemainingCollateralUsd >= minCollateralUsdForLeverage;

    if (!willCollateralBeSufficient) {
      if (
        estimatedRemainingCollateralUsd + estimatedRemainingPnl < minCollateralUsd ||
        position.sizeInUsd - sizeDeltaUsd < minPositionSizeUsd
      ) {
        return true;
      }
    }
  }

  return false;
}

export function getMinCollateralUsdForLeverage(position: PositionInfoLoaded, openInterestDelta: bigint) {
  const minCollateralFactor = getMinCollateralFactorForPosition(position, openInterestDelta);
  return applyFactor(position.sizeInUsd, minCollateralFactor);
}

export function getMinRequiredCollateralUsdForPosition(p: {
  position: PositionInfoLoaded;
  minCollateralUsd: bigint | undefined;
  userReferralInfo: UserReferralInfo | undefined;
}): bigint {
  const { position, minCollateralUsd, userReferralInfo } = p;

  const minCollateralUsdForLeverage = getMinCollateralUsdForLeverage(position, 0n);
  let minRequiredNextCollateralUsd = minCollateralUsdForLeverage;

  if (minCollateralUsd !== undefined && minCollateralUsd > minRequiredNextCollateralUsd) {
    minRequiredNextCollateralUsd = minCollateralUsd;
  }

  const minCollateralUsdForLiquidationPrice = getMinCollateralUsdForLiquidationPrice({
    sizeInUsd: position.sizeInUsd,
    sizeInTokens: position.sizeInTokens,
    marketInfo: position.marketInfo,
    pendingImpactAmount: position.pendingImpactAmount,
    minCollateralUsd: minCollateralUsd ?? 0n,
    pnl: position.pnl,
    isLong: position.isLong,
    userReferralInfo,
  });

  if (minCollateralUsdForLiquidationPrice > minRequiredNextCollateralUsd) {
    minRequiredNextCollateralUsd = minCollateralUsdForLiquidationPrice;
  }

  return (
    minRequiredNextCollateralUsd + (position.pendingBorrowingFeesUsd ?? 0n) + (position.pendingFundingFeesUsd ?? 0n)
  );
}

export function getMaxWithdrawAmount(p: {
  position: PositionInfoLoaded;
  minCollateralUsd: bigint | undefined;
  collateralPrice: bigint | undefined;
  collateralDecimals: number | undefined;
  userReferralInfo: UserReferralInfo | undefined;
}): bigint {
  const { position, minCollateralUsd, collateralPrice, collateralDecimals, userReferralInfo } = p;

  const minRequiredCollateralUsd = getMinRequiredCollateralUsdForPosition({
    position,
    minCollateralUsd,
    userReferralInfo,
  });

  if (position.collateralUsd < minRequiredCollateralUsd) {
    return 0n;
  }

  const maxWithdrawUsd = position.collateralUsd - minRequiredCollateralUsd;

  return convertToTokenAmount(maxWithdrawUsd, collateralDecimals, collateralPrice) ?? 0n;
}

export function payForCollateralCost(p: {
  initialCostUsd: bigint;
  collateralToken: TokenData;
  collateralPrice: bigint;
  outputAmount: bigint;
  remainingCollateralAmount: bigint;
}) {
  const { initialCostUsd, collateralToken, collateralPrice, outputAmount, remainingCollateralAmount } = p;

  const values = {
    outputAmount: BigInt(outputAmount),
    remainingCollateralAmount: BigInt(remainingCollateralAmount),
    paidOutputAmount: 0n,
    paidRemainingCollateralAmount: 0n,
  };

  let remainingCostAmount = convertToTokenAmount(initialCostUsd, collateralToken.decimals, collateralPrice)!;

  if (remainingCostAmount == 0n) {
    return values;
  }

  if (values.outputAmount > 0) {
    if (values.outputAmount > remainingCostAmount) {
      values.outputAmount = values.outputAmount - remainingCostAmount;
      values.paidOutputAmount = remainingCostAmount;
      remainingCostAmount = 0n;
    } else {
      remainingCostAmount = remainingCostAmount - values.outputAmount;
      values.paidOutputAmount = values.outputAmount;
      values.outputAmount = 0n;
    }
  }

  if (remainingCostAmount == 0n) {
    return values;
  }

  if (values.remainingCollateralAmount > remainingCostAmount) {
    values.remainingCollateralAmount = values.remainingCollateralAmount - remainingCostAmount;
    values.paidRemainingCollateralAmount = remainingCostAmount;
    remainingCostAmount = 0n;
  } else {
    remainingCostAmount = remainingCostAmount - remainingCollateralAmount;
    values.paidRemainingCollateralAmount = values.remainingCollateralAmount;
    values.remainingCollateralAmount = 0n;
  }

  return values;
}

function applyAcceptablePrice(p: {
  position: PositionInfoLoaded | undefined;
  marketInfo: MarketInfo;
  isLong: boolean;
  isTrigger: boolean;
  fixedAcceptablePriceImpactBps?: bigint;
  acceptablePriceImpactBuffer?: number;
  values: DecreasePositionAmounts;
  isSetAcceptablePriceImpactEnabled: boolean;
}) {
  const {
    position,
    marketInfo,
    isLong,
    values,
    isTrigger,
    fixedAcceptablePriceImpactBps,
    acceptablePriceImpactBuffer,
    isSetAcceptablePriceImpactEnabled,
  } = p;

  const acceptablePriceInfo = getAcceptablePriceInfo({
    marketInfo,
    isIncrease: false,
    isLimit: false,
    isLong,
    indexPrice: values.indexPrice,
    sizeDeltaUsd: values.sizeDeltaUsd,
  });

  values.acceptablePrice = acceptablePriceInfo.acceptablePrice;
  values.acceptablePriceDeltaBps = acceptablePriceInfo.acceptablePriceDeltaBps;
  values.balanceWasImproved = acceptablePriceInfo.balanceWasImproved;

  const totalImpactValues = getNetPriceImpactDeltaUsdForDecrease({
    marketInfo,
    sizeInUsd: position?.sizeInUsd ?? 0n,
    pendingImpactAmount: position?.pendingImpactAmount ?? 0n,
    sizeDeltaUsd: values.sizeDeltaUsd,
    priceImpactDeltaUsd: acceptablePriceInfo.priceImpactDeltaUsd,
  });

  values.closePriceImpactDeltaUsd = acceptablePriceInfo.priceImpactDeltaUsd;
  values.totalPendingImpactDeltaUsd = totalImpactValues.totalImpactDeltaUsd;
  values.proportionalPendingImpactDeltaUsd = totalImpactValues.proportionalPendingImpactDeltaUsd;
  values.priceImpactDiffUsd = totalImpactValues.priceImpactDiffUsd;

  if (isTrigger) {
    if (!isSetAcceptablePriceImpactEnabled || values.triggerOrderType === OrderType.StopLossDecrease) {
      values.acceptablePrice = isLong ? 0n : MaxUint256;
    } else {
      let maxNegativePriceImpactBps = fixedAcceptablePriceImpactBps;
      values.recommendedAcceptablePriceDeltaBps = getDefaultAcceptablePriceImpactBps({
        isIncrease: false,
        isLong,
        indexPrice: values.indexPrice,
        sizeDeltaUsd: values.sizeDeltaUsd,
        priceImpactDeltaUsd: values.closePriceImpactDeltaUsd,
        acceptablePriceImpactBuffer: acceptablePriceImpactBuffer || DEFAULT_ACCEPTABLE_PRICE_IMPACT_BUFFER,
      });

      if (maxNegativePriceImpactBps === undefined) {
        maxNegativePriceImpactBps = values.recommendedAcceptablePriceDeltaBps;
      }

      const triggerAcceptablePriceInfo = getAcceptablePriceInfo({
        marketInfo,
        isIncrease: false,
        isLimit: false,
        isLong,
        indexPrice: values.indexPrice,
        sizeDeltaUsd: values.sizeDeltaUsd,
        maxNegativePriceImpactBps,
      });

      values.acceptablePrice = triggerAcceptablePriceInfo.acceptablePrice;
      values.acceptablePriceDeltaBps = triggerAcceptablePriceInfo.acceptablePriceDeltaBps;
    }
  }

  return values;
}

export function estimateCollateralCost(baseUsd: bigint, collateralToken: TokenData, collateralPrice: bigint) {
  const amount = convertToTokenAmount(baseUsd, collateralToken.decimals, collateralToken.prices.minPrice)!;
  const usd = convertToUsd(amount, collateralToken.decimals, collateralPrice)!;

  return {
    amount,
    usd,
  };
}

export function getTotalFeesUsdForDecrease({
  positionFeeUsd,
  borrowingFeeUsd,
  fundingFeeUsd,
  swapProfitFeeUsd,
  swapUiFeeUsd,
  uiFeeUsd,
  pnlUsd,
  totalPendingImpactDeltaUsd,
}: {
  positionFeeUsd: bigint;
  borrowingFeeUsd: bigint;
  fundingFeeUsd: bigint;
  swapProfitFeeUsd: bigint;
  swapUiFeeUsd: bigint;
  uiFeeUsd: bigint;
  pnlUsd: bigint;
  totalPendingImpactDeltaUsd: bigint;
}) {
  const negativePriceImpactUsd = totalPendingImpactDeltaUsd < 0 ? bigMath.abs(totalPendingImpactDeltaUsd) : 0n;
  const negativePnlUsd = pnlUsd < 0 ? bigMath.abs(pnlUsd) : 0n;

  const totalFeesUsd =
    positionFeeUsd +
    borrowingFeeUsd +
    fundingFeeUsd +
    swapProfitFeeUsd +
    swapUiFeeUsd +
    uiFeeUsd +
    negativePnlUsd +
    negativePriceImpactUsd;

  return totalFeesUsd;
}

export function getNextPositionValuesForDecreaseTrade(p: {
  existingPosition?: PositionInfo;
  marketInfo: MarketInfo;
  collateralToken: TokenData;
  sizeDeltaUsd: bigint;
  sizeDeltaInTokens: bigint;
  realizedPnl: bigint;
  estimatedPnl: bigint;
  collateralDeltaUsd: bigint;
  collateralDeltaAmount: bigint;
  payedRemainingCollateralUsd: bigint;
  payedRemainingCollateralAmount: bigint;
  proportionalPendingImpactDeltaUsd: bigint;
  showPnlInLeverage: boolean;
  isLong: boolean;
  minCollateralUsd: bigint;
  userReferralInfo: UserReferralInfo | undefined;
}): NextPositionValues {
  const {
    existingPosition,
    marketInfo,
    collateralToken,
    sizeDeltaUsd,
    sizeDeltaInTokens,
    realizedPnl,
    estimatedPnl,
    collateralDeltaUsd,
    collateralDeltaAmount,
    payedRemainingCollateralUsd,
    payedRemainingCollateralAmount,
    showPnlInLeverage,
    proportionalPendingImpactDeltaUsd,
    isLong,
    minCollateralUsd,
    userReferralInfo,
  } = p;

  const nextSizeUsd = existingPosition ? existingPosition.sizeInUsd - sizeDeltaUsd : 0n;
  const nextSizeInTokens = existingPosition ? existingPosition.sizeInTokens - sizeDeltaInTokens : 0n;

  let nextCollateralUsd = existingPosition
    ? existingPosition.collateralUsd - collateralDeltaUsd - payedRemainingCollateralUsd
    : 0n;

  if (nextCollateralUsd < 0) {
    nextCollateralUsd = 0n;
  }

  let nextCollateralAmount = existingPosition
    ? existingPosition.collateralAmount - collateralDeltaAmount - payedRemainingCollateralAmount
    : 0n;

  if (nextCollateralAmount < 0) {
    nextCollateralAmount = 0n;
  }

  const nextPnl = estimatedPnl ? estimatedPnl - realizedPnl : 0n;

  const nextPnlPercentage = nextCollateralUsd !== 0n ? getBasisPoints(nextPnl, nextCollateralUsd) : 0n;

  const nextLeverage = getLeverage({
    sizeInUsd: nextSizeUsd,
    collateralUsd: nextCollateralUsd,
    pnl: showPnlInLeverage ? nextPnl : undefined,
    pendingBorrowingFeesUsd: 0n, // deducted on order
    pendingFundingFeesUsd: 0n, // deducted on order
  });

  const nextLiqPrice = getLiquidationPrice({
    marketInfo,
    collateralToken,
    sizeInTokens: nextSizeInTokens,
    sizeInUsd: nextSizeUsd,
    collateralUsd: nextCollateralUsd,
    collateralAmount: nextCollateralAmount,
    minCollateralUsd,
    userReferralInfo,
    pendingImpactAmount: existingPosition?.pendingImpactAmount ?? 0n,
    pendingBorrowingFeesUsd: 0n, // deducted on order
    pendingFundingFeesUsd: 0n, // deducted on order
    isLong: isLong,
  });

  const nextPendingImpactDeltaUsd =
    existingPosition?.pendingImpactUsd !== undefined && proportionalPendingImpactDeltaUsd !== undefined
      ? existingPosition.pendingImpactUsd - proportionalPendingImpactDeltaUsd
      : 0n;

  return {
    nextSizeUsd,
    nextCollateralUsd,
    nextLiqPrice,
    nextPnl,
    nextPnlPercentage,
    nextLeverage,
    nextPendingImpactDeltaUsd,
  };
}

export function getDecreaseSwapType(pnlToken: TokenData, collateralToken: TokenData, receiveToken: TokenData) {
  if (getIsEquivalentTokens(pnlToken, collateralToken)) {
    return DecreasePositionSwapType.NoSwap;
  } else if (getIsEquivalentTokens(pnlToken, receiveToken)) {
    return DecreasePositionSwapType.SwapCollateralTokenToPnlToken;
  } else {
    return DecreasePositionSwapType.SwapPnlTokenToCollateralToken;
  }
}

export function isThirdTokenDecreaseSwap(
  pnlToken: TokenData,
  collateralToken: TokenData,
  receiveToken: TokenData
): boolean {
  return (
    !getIsEquivalentTokens(receiveToken, pnlToken) &&
    !getIsEquivalentTokens(receiveToken, collateralToken) &&
    !getIsEquivalentTokens(pnlToken, collateralToken)
  );
}

export function getCollateralToPnlInternalSwapStats(p: { pnlToken: TokenData; receiveUsd: bigint }): {
  amountOut: bigint;
} {
  const { pnlToken, receiveUsd } = p;

  // Fees already deducted in getDecreasePositionAmounts — convert at oracle price to avoid double-counting
  const amountOut = convertToTokenAmount(receiveUsd, pnlToken.decimals, pnlToken.prices.minPrice) ?? 0n;

  return { amountOut };
}

export function selectBetterDecreaseSwapType(p: { pathAUsdOut: bigint; pathBUsdOut: bigint }): "pathA" | "pathB" {
  return p.pathBUsdOut > p.pathAUsdOut ? "pathB" : "pathA";
}

export function getOptimalDecreaseAndSwapAmounts(p: {
  marketInfo: MarketInfo;
  collateralToken: TokenData;
  isLong: boolean;
  position: PositionInfoLoaded | undefined;
  closeSizeUsd: bigint;
  keepLeverage: boolean;
  triggerPrice?: bigint;
  fixedAcceptablePriceImpactBps?: bigint;
  acceptablePriceImpactBuffer?: number;
  userReferralInfo: UserReferralInfo | undefined;
  minCollateralUsd: bigint;
  minPositionSizeUsd: bigint;
  uiFeeFactor: bigint;
  triggerOrderType?: DecreasePositionAmounts["triggerOrderType"];
  isSetAcceptablePriceImpactEnabled: boolean;
  receiveToken: TokenData;
  forceDecreaseSwapType?: DecreasePositionSwapType;
  findSwapPath: FindSwapPath;
  findSwapPathFromPnl: FindSwapPath;
  marketsInfoData: MarketsInfoData | undefined;
  chainId: number;
}): {
  decreaseAmounts: DecreasePositionAmounts;
  swapAmounts: SwapAmounts | undefined;
} {
  const {
    marketInfo,
    collateralToken,
    isLong,
    position,
    closeSizeUsd,
    keepLeverage,
    triggerPrice,
    fixedAcceptablePriceImpactBps,
    acceptablePriceImpactBuffer,
    userReferralInfo,
    minCollateralUsd,
    minPositionSizeUsd,
    uiFeeFactor,
    triggerOrderType,
    isSetAcceptablePriceImpactEnabled,
    receiveToken,
    forceDecreaseSwapType,
    findSwapPath,
    findSwapPathFromPnl,
    marketsInfoData,
    chainId,
  } = p;

  const decreaseBaseParams = {
    marketInfo,
    collateralToken,
    isLong,
    position,
    closeSizeUsd,
    keepLeverage,
    triggerPrice,
    fixedAcceptablePriceImpactBps,
    acceptablePriceImpactBuffer,
    userReferralInfo,
    minCollateralUsd,
    minPositionSizeUsd,
    uiFeeFactor,
    triggerOrderType,
    isSetAcceptablePriceImpactEnabled,
    receiveToken,
  };

  if (forceDecreaseSwapType !== undefined) {
    return {
      decreaseAmounts: getDecreasePositionAmounts({
        ...decreaseBaseParams,
        forceDecreaseSwapType,
      }),
      swapAmounts: undefined,
    };
  }

  const pathADecrease = getDecreasePositionAmounts(decreaseBaseParams);

  const pnlToken = isLong ? marketInfo.longToken : marketInfo.shortToken;
  const shouldSwap = !getIsEquivalentTokens(collateralToken, receiveToken);

  if (!shouldSwap) {
    return { decreaseAmounts: pathADecrease, swapAmounts: undefined };
  }

  const isThirdToken = isThirdTokenDecreaseSwap(pnlToken, collateralToken, receiveToken);

  const pathASwap =
    pathADecrease.receiveTokenAmount > 0n
      ? getSwapAmountsByFromValue({
          tokenIn: collateralToken,
          tokenOut: receiveToken,
          amountIn: pathADecrease.receiveTokenAmount,
          isLimit: false,
          findSwapPath,
          uiFeeFactor,
          marketsInfoData,
          chainId,
          externalSwapQuoteParams: undefined,
          allowSameTokenSwap: false,
        })
      : undefined;

  if (!isThirdToken) {
    return { decreaseAmounts: pathADecrease, swapAmounts: pathASwap };
  }

  // Path B: force internal collateral→pnl swap, then external pnl→receive
  const pathBDecrease = getDecreasePositionAmounts({
    ...decreaseBaseParams,
    forceDecreaseSwapType: DecreasePositionSwapType.SwapCollateralTokenToPnlToken,
  });

  if (pathBDecrease.primaryOutput.usd <= 0n) {
    return { decreaseAmounts: pathADecrease, swapAmounts: pathASwap };
  }

  const internalSwapStats = getCollateralToPnlInternalSwapStats({
    pnlToken,
    receiveUsd: pathBDecrease.primaryOutput.usd,
  });

  if (internalSwapStats.amountOut <= 0n) {
    return { decreaseAmounts: pathADecrease, swapAmounts: pathASwap };
  }

  const pathBSwap = getSwapAmountsByFromValue({
    tokenIn: pnlToken,
    tokenOut: receiveToken,
    amountIn: internalSwapStats.amountOut,
    isLimit: false,
    findSwapPath: findSwapPathFromPnl,
    uiFeeFactor,
    marketsInfoData,
    chainId,
    externalSwapQuoteParams: undefined,
    allowSameTokenSwap: false,
  });

  const winner = selectBetterDecreaseSwapType({
    pathAUsdOut: pathASwap?.usdOut ?? 0n,
    pathBUsdOut: pathBSwap.usdOut,
  });

  if (winner === "pathB") {
    return { decreaseAmounts: pathBDecrease, swapAmounts: pathBSwap };
  }

  return { decreaseAmounts: pathADecrease, swapAmounts: pathASwap };
}
